import { useCallback, useRef, useState } from "react";
import { useStore } from "react-redux";

import { useCheckAnswersMutation, useLazyGetAssignmentAnswersQuery } from "api/generated";
import type { QuestionAutoInput, UserAssignmentForExerciseFragment } from "api/generated";
import { useAppDispatch } from "store/hooks";
import { setPoints } from "store/slices/exercise";
import { getAssignmentTime, resetAssignmentTimer, setIsTimerShown, startAssignmentTimer } from "store/slices/timer";
import type { RootState } from "store/store";
import { logToSentry, processApiError, processExerciseError } from "utils/processApiError";
import { processAnswerResponse } from "utils/processAnswerResponse";

// po otázkách, v pořadí otázek
export type CorrectAnswers = Array<Array<string | null>>;

interface IUseAnswerCheckParams {
  data: UserAssignmentForExerciseFragment;
  userAssignmentId: string;
  evaluateAnswer: boolean;
}

// AIDEV-NOTE: V tréninku ověří odpověď JEDNÍM požadavkem `checkAnswers` (BE v něm vrací i `correctAnswers`).
// Dřív komponenty posílaly vedle něj i `getAssignmentAnswers`; tlačítko blokovalo `isGetting && isSending`,
// takže po dokončení `checkAnswers` se povolilo i při visícím dotazu a RTK Query stejný pending dotaz
// nezdvojí (`status === "pending"` → nic se neodešle), takže se další kliky vyhodnocení nedočkaly. [TD-481]
// Čas se čte ze store v okamžiku odeslání (`getAssignmentTime(store.getState())`), ne z closure:
// `ExerciseFooter` volá `measurePracticeTime()` a hned `onSubmit()` — closure by ještě držela čas před změřením.
// Onboarding (`evaluateAnswer === false`) nemá session, proto dál jen `getAssignmentAnswers`.
export const useAnswerCheck = ({ data, userAssignmentId, evaluateAnswer }: IUseAnswerCheckParams) => {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const [getAnswers] = useLazyGetAssignmentAnswersQuery();
  const [sendAnswers] = useCheckAnswersMutation();

  const [correctAnswers, setCorrectAnswers] = useState<CorrectAnswers | undefined>(undefined);
  const [points, setPointsState] = useState<number | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isErrorModalVisible, setErrorModalVisible] = useState(false);
  const checkingRef = useRef(false);

  const checkOnboarding = async () => {
    if (typeof data.assignment?.id !== "string") return;

    const response = await getAnswers({ assignmentId: data.assignment.id });
    if (response.error !== undefined) {
      processApiError(response.error);
      setErrorModalVisible(true);

      return;
    }

    const questions = response.data?.assignments[0]?.assignment?.questions;
    if (questions !== undefined) setCorrectAnswers(questions.map(question => question.correctAnswers));
  };

  const checkTraining = async (userAnswers: QuestionAutoInput[]) => {
    const timeToSolve = getAssignmentTime(store.getState());

    try {
      const response = await sendAnswers({
        assignmentId: userAssignmentId,
        selfEvaluation: null,
        userAnswers,
        timeToSolve,
      });
      processAnswerResponse(response, data);

      if ("data" in response && response.data) {
        const { scoreReal, correctAnswers: answers } = response.data.answer;
        dispatch(setPoints({ userAssignmentId, points: scoreReal }));
        setPointsState(scoreReal);

        if (answers === null || answers === undefined) {
          // BE ho u `answer` plní vždy; bez něj by student zůstal potichu zaseknutý.
          logToSentry(new Error("checkAnswers nevrátil correctAnswers"), { assignmentId: userAssignmentId });
          setErrorModalVisible(true);
        } else {
          setCorrectAnswers(answers);
          setErrorModalVisible(false);
        }
      } else if ("error" in response) {
        processExerciseError(response.error, {
          assignmentId: userAssignmentId,
          selfEvaluation: null,
          userAnswers: userAnswers.flatMap(answer => answer.answers),
          timeToSolve,
        });
        setErrorModalVisible(true);
      }
    } finally {
      dispatch(resetAssignmentTimer());
      dispatch(startAssignmentTimer());
    }
  };

  // Bez useCallback: komponenty ho volají jen z onSubmit, stabilní reference nikdo nepotřebuje.
  const check = (userAnswers: QuestionAutoInput[] | undefined) => {
    if (checkingRef.current) return;
    if (evaluateAnswer && userAnswers === undefined) return;
    if (!evaluateAnswer && typeof data.assignment?.id !== "string") return;

    checkingRef.current = true;
    setIsChecking(true);
    dispatch(setIsTimerShown(false));

    const run = evaluateAnswer && userAnswers !== undefined ? checkTraining(userAnswers) : checkOnboarding();
    run
      .catch((error: unknown) => {
        console.error(error);
      })
      .finally(() => {
        checkingRef.current = false;
        setIsChecking(false);
      });
  };

  const hideErrorModal = useCallback(() => {
    setErrorModalVisible(false);
  }, []);

  return { correctAnswers, points, check, isChecking, isErrorModalVisible, hideErrorModal };
};
